import { useState } from 'react';
import DateGeneric from '../../custom-ui/old-ui/DateGeneric';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { getJobsByMakeLiveDateRange } from '../../../data/jobDetailsNewCollection';

import { Timestamp } from "firebase/firestore";
import ToastAlert from '../../custom-ui/toast-alert/ToastAlert';
import { csvDownload } from './ExportCsv';
import DownloadIconWithLabel from './DownLoadIcon';

// Zod schema to validate date range with custom refinement
const dateRangeSchema = z
  .object({
    startDate: z.date({ required_error: 'Start date is required' }),
    endDate: z.date({ required_error: 'End date is required' }),
  })
  .refine(
    (data) => data.endDate >= data.startDate,
    {
      path: ['endDate'],
      message: 'End date cannot be before start date',
    }
  );

const JobStatsDownloadByDate = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleDownload = async () => {
    setLoading(true);
    setFormError(null);

    const result = dateRangeSchema.safeParse({ startDate, endDate });

    if (!result.success) {
      const issue = result.error.errors[0];
      setFormError(issue.message);
      setLoading(false);
      return;
    }

    const startTimestamp = Timestamp.fromDate(result.data.startDate);
    const endTimestamp = Timestamp.fromDate(result.data.endDate);

    try {
      const jobs = await getJobsByMakeLiveDateRange(startTimestamp, endTimestamp);

      const filteredData = jobs.filter((job: any) => {
        if (!job?.makeLiveDate) return false;

        const jobTimestamp = job.makeLiveDate.seconds;
        return jobTimestamp >= startTimestamp.seconds && jobTimestamp <= endTimestamp.seconds;
      });

      const finalDataToDownload = filteredData.map((job: any) => ({
        "Job Title": job?.jobName,
        "Employer Name": job?.companyFullName,
        "View Count": job?.clicksCounter,
        "Apply Now Counter": job?.applyNowCounter,
      }));

      if (finalDataToDownload.length > 0) {
        toast.success('CSV downloaded successfully.');
        csvDownload(finalDataToDownload, "JobsStats_by_date_range");
      } else {
        toast.error('No data found for selected date range.');
      }

    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className='p-4 bg-white rounded-xl'>
        <p className='text-instant-teams-blue-Main text-xl font-semibold my-2 mb-4'>Job Stats</p>
        <div className='w-full grid grid-cols-5 space-x-2  items-center'>
          <div className=" col-span-2 ">
            <DateGeneric
              name="startDate"
              display="Start Date"
              value={startDate}
              onChange={setStartDate}
              timestamp={true}
              required
              dateLimit="past"
            />
          </div>
          <div className=" col-span-2 ">
            <DateGeneric
              name="endDate"
              display="End Date"
              value={endDate}
              onChange={setEndDate}
              timestamp={true}
              required
              dateLimit="past"
            />
          </div>
          <div className=' space-x-2  mt-[-15px]  col-span-1 items-center'>
            <button
              className='bg-instant-teams-blue-Main mr-[-20px] rounded-md flex items-center text-white px-2 py-3  '
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? "Downloading..." : "Download List"}
              <DownloadIconWithLabel  isFromEmpMetric={true} tooltipText={"Export Jobs Stats"}/>
            </button>
          </div>
        </div>

        {formError && (
          <p className='text-sm mt-[-10px] text-red-500 '>{formError==="Expected date, received null"?"Please select a start and end Date":formError}</p>
        )}
      </div>
      <ToastAlert />
    </>
  );
};

export default JobStatsDownloadByDate;
